import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useThemeContext } from '../context/ThemeProvider';
import DrumsetEditor from './DrumsetEditor';
import SoundManagementPanel from './SoundManagementPanel';
import type { DrumsetConfig, DrumInstrument } from '../types';

interface DrumsetEditorPanelProps {
  onToggleDrumsetEditor: () => void;
  showDrumsetEditor: boolean;
  onCloseDrumsetEditor: () => void;
  onDrumsetSave: (config: DrumsetConfig) => void;
  onSoundManagementSave: (instruments: DrumInstrument[], kitName?: string) => void;
  drumsetConfig?: DrumsetConfig | null;
  showDynamicMessage?: (message: string, type?: "info" | "success" | "warning" | "error") => void;
  debugMode: boolean;
  currentInstruments?: DrumInstrument[];
}

export default function DrumsetEditorPanel({
  onToggleDrumsetEditor,
  showDrumsetEditor,
  onCloseDrumsetEditor,
  onDrumsetSave,
  onSoundManagementSave,
  drumsetConfig,
  showDynamicMessage,
  debugMode,
  currentInstruments
}: DrumsetEditorPanelProps) {
  const { t } = useLanguage();
  const { getClass } = useThemeContext();
  const [showSoundManagement, setShowSoundManagement] = useState<boolean>(false);

  const getThemeClass = (type: string): string => {
    try {
      return getClass(type) || '';
    } catch {
      const fallback: Record<string, string> = {
        background: 'bg-white',
        text: 'text-gray-900',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        grid: 'bg-gray-100',
        border: 'border-gray-300',
        input: 'border-gray-300 bg-white text-gray-900',
        card: 'bg-white border-gray-200 shadow-sm',
      };
      return fallback[type] || '';
    }
  };

  const handleSoundManagementSave = (newInstruments: DrumInstrument[], kitName?: string): void => {
    onSoundManagementSave(newInstruments, kitName);
    setShowSoundManagement(false);
  };

  return (
    <>
      {/* DrumsetEditor Panel */}
      <div className={`p-3 rounded ${getThemeClass('grid')} ${getThemeClass('border')}`}>
        {!showDrumsetEditor && (
          <div className="flex items-center space-x-3">
            <h2 className="text-lg md:text-xl font-bold">🛠️ {t('drumsetTitle') || 'Drumset Editor'}</h2>
            <button
              onClick={() => setShowSoundManagement(true)}
              className={`px-3 py-1 rounded text-sm flex items-center space-x-1 transition-all duration-200 hover:scale-105 active:scale-95 transform-gpu ${getThemeClass('button')}`}
            >
              <span className="hidden md:inline">🎵</span>
              <span className="hidden md:inline">{t('soundManagement')}</span>
              <span className="md:hidden">🎵</span>
            </button>
            {debugMode && (
              <button
                onClick={onToggleDrumsetEditor}
                className={`px-3 py-1 rounded text-sm flex items-center space-x-1 transition-all duration-200 hover:scale-105 active:scale-95 transform-gpu ${getThemeClass('button')}`}
              >
                <span>⚙️</span>
                <span>{t('showDrumsetEditor') || 'Show'}</span>
              </button>
            )}
          </div>
        )}
        
        {showDrumsetEditor && (
          <>
            <div className="flex items-center space-x-3 mb-4">
              <h2 className="text-lg md:text-xl font-bold">🛠️ {t('drumsetTitle') || 'Drumset Editor'}</h2>
              {debugMode && (
                <button
                  onClick={onToggleDrumsetEditor}
                  className={`px-3 py-1 rounded text-sm flex items-center space-x-1 transition-all duration-200 hover:scale-105 active:scale-95 transform-gpu ${getThemeClass('button')}`}
                >
                  <span>❌</span>
                  <span>{t('hideDrumsetEditor') || 'Hide'}</span>
                </button>
              )}
            </div>
            <DrumsetEditor 
              onSave={onDrumsetSave}
              onBack={onCloseDrumsetEditor}
              currentConfig={drumsetConfig}
              showDynamicMessage={showDynamicMessage}
            />
          </>
        )}
      </div>

      {/* Sound Management Panel */}
      {showSoundManagement && (
        <SoundManagementPanel
          onClose={() => setShowSoundManagement(false)}
          onSaveKit={handleSoundManagementSave}
          showDynamicMessage={showDynamicMessage}
          currentInstruments={currentInstruments}
          drumsetConfig={drumsetConfig}
        />
      )}
    </>
  );
} 